import { v4 as uuidv4 } from "uuid";
import {
  ConfirmationPopup,
  type ConfirmationPopupProps,
} from "components/popups/ConfirmationPopup";
import React, {
  createContext,
  type Dispatch,
  isValidElement,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { type PopupProps } from "components/popups/Popup";
import {
  deleteQueryParams,
  getQueryParam,
  setQueryParams,
} from "utils/helpers/url";
import classNames from "classnames";

export type PopupData = { excludeFromUrl?: boolean } & {
  popup: "confirm";
  props: ConfirmationPopupProps;
};

type State = {
  popups: Array<PopupData | { popup: React.ReactElement; props: PopupProps }>;
};

const initialState: State = {
  popups: [],
};

type PopupProviderProps = PropsWithChildren<{
  searchParamsKey?: string;
  global?: boolean;
}>;

const PopupContext = createContext({} as typeof initialState);
const PopupDispatchContext = createContext<Dispatch<SetStateAction<State>>>(
  null as any
);

const PopupContextGlobal = createContext({} as typeof initialState);
const PopupDispatchContextGlobal = createContext<
  Dispatch<SetStateAction<State>>
>(null as any);

const PopupProvider: React.FC<PopupProviderProps> = ({
  children,
  global,
  searchParamsKey = "popups",
}) => {
  const [state, setState] = useState(initialState);

  let Context, DispatchContext;

  if (global) {
    Context = PopupContextGlobal;
    DispatchContext = PopupDispatchContextGlobal;
  } else {
    Context = PopupContext;
    DispatchContext = PopupDispatchContext;
  }

  useEffect(() => {
    const sPopups = getQueryParam(searchParamsKey);
    if (sPopups) {
      setState((prev) => ({ ...prev, popups: JSON.parse(atob(sPopups)) })); //NOSONAR
    }

    return () => {
      deleteQueryParams(searchParamsKey);
    };
  }, []);

  useEffect(() => {
    if (state.popups.length) {
      setQueryParams(
        searchParamsKey,
        btoa(
          //NOSONAR
          JSON.stringify(
            state.popups.filter(
              (data) =>
                !isValidElement(data.popup) &&
                !(data as PopupData).excludeFromUrl
            )
          )
        )
      );
    } else {
      deleteQueryParams(searchParamsKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.popups]);

  const renderPopups = useCallback(() => {
    // const popups = { ...ENTITY_POPUPS, ...POPUPS };

    return state.popups.map((p, i) => {
      if (isValidElement(p.popup)) {
        return (
          <React.Fragment
            key={i} //NOSONAR
          >
            {React.cloneElement(p.popup, {
              isLast: i === state.popups.length - 1,
              ...p.props,
            } as PopupProps)}
          </React.Fragment>
        );
      }

      p = p as PopupData;

      // const PopupComponent = popups[p.popup];

      // if (!PopupComponent) {
      //   return <></>;
      // }

      // return (
      //   <PopupComponent
      //     isLast={i === state.popups.length - 1}
      //     addNavigateToHistory
      //     key={i}
      //     portal={false}
      //     {...(p.props as any)}
      //   />
      // );
    });
  }, [state.popups]);

  return (
    <DispatchContext.Provider value={setState}>
      <Context.Provider value={state}>
        <div className="overlay">
          <div
            id="popup-root"
            className={classNames("window_container", {
              open: state.popups.length,
            })}
          >
            {renderPopups()}
          </div>
        </div>
        {children}
      </Context.Provider>
    </DispatchContext.Provider>
  );
};

export const usePopup = (global?: boolean) => {
  let dispatch = useContext(
    global ? PopupDispatchContextGlobal : PopupDispatchContext
  );

  const globalDispatch = useContext(PopupDispatchContextGlobal);

  if (!dispatch) {
    dispatch = globalDispatch;
  }

  const openPopup = useCallback(
    (data: PopupData | React.ReactElement) => {
      const popupKey = uuidv4();
      if (isValidElement(data)) {
        dispatch((prev) => ({
          ...prev,
          popups: [
            ...prev.popups,
            { popup: data, props: { global: global, popupKey } },
          ],
        }));
        return;
      }
      const popupData = data as PopupData;
      dispatch((prev) => ({
        ...prev,
        popups: [
          ...prev.popups,
          {
            ...popupData,
            props: { global: global, popupKey, ...(popupData.props as any) },
          },
        ],
      }));
    },
    [dispatch, global]
  );

  const closeLastPopup = useCallback(() => {
    dispatch((prev) => ({
      ...prev,
      popups: prev.popups.slice(0, prev.popups.length - 1),
    }));
  }, [dispatch]);

  const closePopup = useCallback(
    (key: string) => {
      dispatch((prev) => {
        return {
          ...prev,
          popups: prev.popups.filter((p) => {
            if (!p.props) {
              return true;
            }

            const props = p.props as PopupProps;

            if (props.popupKey && props.popupKey !== key) {
              return true;
            }

            return false;
          }),
        };
      });
    },
    [dispatch]
  );

  return { openPopup, closeLastPopup, closePopup };
};

export const usePopupState = (global?: boolean) => {
  const { popups } = useContext(global ? PopupContextGlobal : PopupContext);
  return { popups };
};

export const useConfirm = (global?: boolean) => {
  const { openPopup } = usePopup(global);

  const confirm = useCallback(
    (props: Partial<ConfirmationPopupProps>) => {
      openPopup(
        <ConfirmationPopup
          {...props}
          positiveCallback={() => {
            props.positiveCallback?.();
          }}
        />
      );
    },
    [openPopup]
  );

  return { confirm };
};

export default PopupProvider;
